import {
  Form,
  Button,
  message,
  Flex,
  Row,
  Col,
  Checkbox,
  Input,
  Alert,
  Radio,
} from "antd";
import { useForm } from "antd/lib/form/Form";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  IconArrowNarrowLeft,
  IconBrandYoutube,
  IconBrandInstagram,
  IconExternalLink,
} from "@tabler/icons-react";

import Loader from "../../components/Loader";
import SectionCard from "../../components/SectionCard";
import CustomImageUploader from "../../components/CustomImageUploader";
import UploadUtils from "../../components/Upload";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { EventData } from "../../models/event";
import { updateCall } from "../../constants/apicalls.constant";
import { setSelectedEventList } from "../../store/slices/event.slice";
import { ROUTES } from "../../constants/routes.constant";
import { RootState } from "../../store/store";
import { isEventUnderAdminReview } from "../../utils/eventEditAccess";
import ReactQuill from "react-quill-new";
import styles from "../../components/DescriptionArea.module.scss";

const getYouTubeVideoId = (url: string): string | null => {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0];
    if (u.hostname.includes("youtube.com")) return u.searchParams.get("v");
    return null;
  } catch {
    return null;
  }
};

const getInstagramEmbedInfo = (
  url: string,
): { type: "post" | "reel"; id: string } | null => {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("instagram.com")) return null;
    const postMatch = u.pathname.match(/^\/p\/([A-Za-z0-9_-]+)/);
    if (postMatch) return { type: "post", id: postMatch[1] };
    const reelMatch = u.pathname.match(/^\/reel\/([A-Za-z0-9_-]+)/);
    if (reelMatch) return { type: "reel", id: reelMatch[1] };
    return null;
  } catch {
    return null;
  }
};

const InstagramEmbed = ({
  type,
  id,
}: {
  type: "post" | "reel";
  id: string;
}) => {
  const permalinkUrl =
    type === "post"
      ? `https://www.instagram.com/p/${id}/`
      : `https://www.instagram.com/reel/${id}/`;

  useEffect(() => {
    const tryProcess = () => {
      if ((window as any).instgrm?.Embeds?.process) {
        (window as any).instgrm.Embeds.process();
        return true;
      }
      return false;
    };

    if (!tryProcess()) {
      const existing = document.getElementById("instagram-embed-script");
      if (!existing) {
        const script = document.createElement("script");
        script.id = "instagram-embed-script";
        script.src = "//www.instagram.com/embed.js";
        script.async = true;
        script.onload = () => tryProcess();
        document.body.appendChild(script);
      }
    }
  }, [id]);

  return (
    <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={`${permalinkUrl}?utm_source=ig_embed`}
        data-instgrm-version="14"
        style={{
          background: "#FFF",
          border: 0,
          borderRadius: 3,
          boxShadow:
            "0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)",
          margin: 0,
          maxWidth: 540,
          width: "100%",
          padding: 0,
        }}
      >
        <div
          style={{ padding: 16, textAlign: "center", color: "#6b7280", fontSize: 13 }}
        >
          Loading Instagram preview…
        </div>
      </blockquote>
    </div>
  );
};

const AddEventDetails = () => {
  const [form] = useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const skipNavigate = useRef(false);
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});
  const [socialLink, setSocialLink] = useState<string>("");
  const [tcType, setTcType] = useState<"link" | "custom">("link");
  const [tcContentMode, setTcContentMode] = useState<"default" | "custom">(
    "default",
  );
  const [rulesContentMode, setRulesContentMode] = useState<
    "default" | "custom"
  >("default");

  const MAX_LENGTH = 5000;
  const tcQuillRef = useRef<ReactQuill | null>(null);
  const rulesQuillRef = useRef<ReactQuill | null>(null);
  const [tcCharCount, setTcCharCount] = useState(0);
  const [rulesCharCount, setRulesCharCount] = useState(0);
  const isAnyImageUploading = Object.values(isUploading).some(Boolean);

  const default_terms: Array<String> = [
    "Registration fees are non-refundable and non-transferable unless approved by the organiser.",
    "Participants must provide accurate information and carry valid ID proof during bib collection if required.",
    "Participants confirm they are medically fit to participate in the event.",
    "The organiser may change the event schedule, venue, route, or timings due to safety or unforeseen circumstances.",
    "The organiser is not responsible for any injury, loss, theft, or damage during the event.",
    "Event photos and videos may be used by the organiser for marketing and promotional purposes.",
  ];

  const default_rules: Array<String> = [
    "Participants must wear their assigned bib visibly throughout the event.",
    "Bib exchange, resale, or unauthorised transfer is strictly prohibited.",
    "Participants must follow all instructions given by race officials, volunteers, and security staff.",
    "Any cheating, misconduct, or use of unfair means may result in disqualification.",
    "Participants must stay on the designated race route and follow safety guidelines.",
    "The organiser's decision regarding event rules, timings, and results shall be final.",
  ];

  const defaultTcText = default_terms.join("\n");
  const defaultRulesText = default_rules.join("\n");

  const renderSocialPreview = (url: string) => {
    if (!url) return null;
    const lower = url.toLowerCase();
    const videoId = getYouTubeVideoId(url);

    if (videoId) {
      return (
        <div
          style={{
            marginTop: 12,
            borderRadius: 8,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
          }}
        >
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            style={{ width: "100%", height: 220, border: "none", display: "block" }}
            allowFullScreen
            title="YouTube preview"
          />
        </div>
      );
    }

    if (lower.includes("youtube.com")) {
      return (
        <div
          style={{
            marginTop: 12,
            padding: "12px 16px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#fff8f8",
          }}
        >
          <div
            style={{
              background: "#FF0000",
              borderRadius: 6,
              padding: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconBrandYoutube size={20} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              YouTube Channel
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                color: "#6c5ce7",
                display: "flex",
                alignItems: "center",
                gap: 4,
                wordBreak: "break-all",
              }}
            >
              {url}
              <IconExternalLink size={12} style={{ flexShrink: 0 }} />
            </a>
          </div>
        </div>
      );
    }

    if (lower.includes("instagram.com")) {
      const embedInfo = getInstagramEmbedInfo(url);
      if (embedInfo) {
        return <InstagramEmbed type={embedInfo.type} id={embedInfo.id} />;
      }
      return (
        <div
          style={{
            marginTop: 12,
            padding: "12px 16px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#fff8ff",
          }}
        >
          <div
            style={{
              background:
                "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
              borderRadius: 6,
              padding: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconBrandInstagram size={20} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              Instagram
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                color: "#6c5ce7",
                display: "flex",
                alignItems: "center",
                gap: 4,
                wordBreak: "break-all",
              }}
            >
              {url}
              <IconExternalLink size={12} style={{ flexShrink: 0 }} />
            </a>
          </div>
        </div>
      );
    }

    return null;
  };

  const handleUploadStatusChange = useCallback(
    (field: string, status: boolean) => {
      setIsUploading((prev) => {
        if (prev[field] === status) return prev;
        return { ...prev, [field]: status };
      });
    },
    [],
  );

  const event = useSelector((state: RootState) => state.event);
  const { loading, trigger } = useAPI<{
    status: boolean;
    message: string;
    data: EventData;
  }>(ENDPOINTS.event);

  const onSubmit = async (values: EventData) => {
    const shouldNavigate = !skipNavigate.current;
    skipNavigate.current = false;
    const getSingleValue = (val: unknown) => {
      const result = UploadUtils.processWithIDUploadImage(val);
      return Array.isArray(result) ? result[0] : result;
    };

    const valueToSend: Partial<EventData> = {
      ...event,
      ...values,
      banner_image: getSingleValue(values.banner_image),
      feature_images: UploadUtils.processWithIDUploadImage(
        values.feature_images,
        true,
      ),
    };

    if (event.steps_info) {
      if (!event.steps_info.includes("event_details")) {
        valueToSend.steps_info = ["event_details", ...event.steps_info];
      }
    } else {
      valueToSend.steps_info = ["event_details"];
    }

    const res = await trigger(updateCall<Partial<EventData>>(id!, valueToSend));

    if (res?.status) {
      dispatch(setSelectedEventList(res?.data));

      if (shouldNavigate) {
        message.success("Event details saved successfully");
        navigate(ROUTES.ADD_TICKET.path.replace(":id", id!));
      } else {
        message.success("Saved as draft");
      }
    } else {
      message.error(res?.message || "Failed to update event details");
    }
  };

  useEffect(() => {
    if (id && event) {
      setSocialLink(event.social_media_link || event.faq_link || "");
      form.setFieldsValue({
        social_media_link: event.social_media_link || event.faq_link,
        terms_link: event.terms_link,
        terms_and_conditions: event.terms_and_conditions,
        rules: event.rules,
        dynamic_qr: event.dynamic_qr,
        banner_image: event.banner_image,
        feature_images: Array.isArray(event.feature_images)
          ? (event.feature_images as unknown[])
              .filter(
                (img) =>
                  typeof img === "object" && img !== null && "uid" in img,
              )
              .map((img: any) => ({
                uid: img.uid,
                name: img.name,
                status: "done",
                thumbUrl: img.thumbnail,
                url: img.url,
              }))
          : [],
      });

      // Determine Terms & Conditions content mode (treat empty or whitespace as default)
      if (!event.terms_and_conditions || !event.terms_and_conditions.trim()) {
        setTcContentMode("default");
        form.setFieldValue("terms_and_conditions", defaultTcText);
      } else if (event.terms_and_conditions === defaultTcText) {
        setTcContentMode("default");
        form.setFieldValue("terms_and_conditions", defaultTcText);
      } else {
        setTcContentMode("custom");
      }

      // Determine Rules content mode (treat empty or whitespace as default)
      if (!event.rules || !event.rules.trim()) {
        setRulesContentMode("default");
        form.setFieldValue("rules", defaultRulesText);
      } else if (event.rules === defaultRulesText) {
        setRulesContentMode("default");
        form.setFieldValue("rules", defaultRulesText);
      } else {
        setRulesContentMode("custom");
      }

      // Set tcType based on determined content mode
      if (tcContentMode === "custom") {
        setTcType("custom");
      } else {
        setTcType("link");
      }
    } else {
      // Initialize defaults for new event
      form.setFieldsValue({
        terms_and_conditions: defaultTcText,
        rules: defaultRulesText,
      });
      setTcContentMode("default");
      setRulesContentMode("default");
    }
  }, [id, event, form, defaultTcText, defaultRulesText]);

  return (
    <Loader spinning={loading}>
    <div className="p-24">
      {/* ── Page Header ── */}
      <div className="flex items-center gap-24 mb-24">
        <Button
          icon={<IconArrowNarrowLeft size={18} />}
          shape="circle"
          variant="filled"
          color="default"
          className="border! shrink-0"
          onClick={() => navigate(`/edit-event/${id}`)}
        />
        <div>
          <h2 className="text-[20px] font-semibold text-[#111827] leading-tight">
            Event Details
          </h2>
          <p className="text-[13px] text-[#6b7280] mt-2">
            Add media, terms, and other details for your event
          </p>
        </div>
      </div>

      <div className="flex justify-center max-w-3xl mx-auto">
        <fieldset
          disabled={isEventUnderAdminReview(event)}
          style={{ border: "none", padding: 0, margin: 0 }}
        >
        <Form
          layout="vertical"
          form={form}
          onFinish={onSubmit}
          disabled={isEventUnderAdminReview(event)}
          scrollToFirstError
        >
          {/* ── Section: Media ── */}
          <SectionCard
            title="Event Media"
            description="Upload a banner and gallery images to make your event stand out"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Form.Item
                  label="Banner Image"
                  name="banner_image"
                  extra={
                    <Alert
                      type="info"
                      message="Recommended: 1920×1080 px (16:9)"
                      showIcon
                      style={{ marginTop: 8 }}
                    />
                  }
                >
                  <CustomImageUploader
                    max={1}
                    form={form}
                    name="banner_image"
                    initialValue={
                      Array.isArray(event?.banner_image)
                        ? (event.banner_image as unknown[]).map((img: any) => ({
                            url: img.url,
                            uid: img.uid,
                            percent: 100,
                            uploading: false,
                          }))
                        : []
                    }
                    onUploadStatusChange={useCallback(
                      (status: boolean) =>
                        handleUploadStatusChange("banner_image", status),
                      [handleUploadStatusChange],
                    )}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label="Gallery Images"
                  name="feature_images"
                  style={{ marginBottom: 0 }}
                  extra={
                    <Alert
                      type="info"
                      message="Upload up to 10 images · JPEG or PNG · max 5 MB each"
                      showIcon
                      style={{ marginTop: 8 }}
                    />
                  }
                >
                  <CustomImageUploader
                    max={10}
                    form={form}
                    name="feature_images"
                    initialValue={
                      Array.isArray(event?.feature_images)
                        ? (event.feature_images as unknown[])
                            .filter(
                              (img: any) =>
                                typeof img === "object" &&
                                img !== null &&
                                img.uid,
                            )
                            .map((img: any) => ({
                              url: img.url,
                              uid: img.uid,
                              percent: 100,
                              uploading: false,
                            }))
                        : []
                    }
                    onUploadStatusChange={useCallback(
                      (status: boolean) =>
                        handleUploadStatusChange("feature_images", status),
                      [handleUploadStatusChange],
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* ── Section: Links ── */}
          <SectionCard
            title="Social Links"
            description="Add your social media page so participants can follow updates"
          >
            <Flex gap={16}>
              <Form.Item
                name="social_media_link"
                label="Social Media Link"
                rules={[
                  { type: "url", message: "Please enter a valid URL" },
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const allowed = [
                        "instagram.com",
                        "youtube.com",
                        "youtu.be",
                      ];
                      const lower = value.toLowerCase();
                      if (allowed.some((domain) => lower.includes(domain))) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        "Only Instagram or YouTube links are allowed",
                      );
                    },
                  },
                ]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input
                  size="large"
                  placeholder={"https://youtube.com/@yourchannel"}
                  onChange={(e) => setSocialLink(e.target.value)}
                />
              </Form.Item>
            </Flex>
            {renderSocialPreview(socialLink)}
          </SectionCard>

          {/* ── Section: Terms & Conditions ── */}
          <SectionCard
            title="Terms & Conditions"
            description="Define the terms participants agree to when registering"
          >
            <Form.Item
              label="How do you want to provide T&C?"
              className="!mb-3"
            >
              <Radio.Group
                value={tcType}
                buttonStyle="solid"
                onChange={(e) => {
                  setTcType(e.target.value);
                  if (e.target.value === "link") {
                    form.setFieldValue("terms_and_conditions", undefined);
                  } else {
                    form.setFieldValue("terms_link", undefined);
                  }
                }}
              >
                <Radio.Button value="link">Use a link</Radio.Button>
                <Radio.Button value="custom">Custom Terms</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {tcType === "link" && (
              <Form.Item
                name="terms_link"
                label="Terms & Conditions URL"
                rules={[{ type: "url", message: "Please enter a valid URL" }]}
                style={{ marginBottom: 0 }}
              >
                <Input size="large" placeholder="https://example.com/terms" />
              </Form.Item>
            )}

            {tcType === "custom" && (
              <Form.Item label="Terms & Conditions" style={{ marginBottom: 0 }}>
                <Flex vertical gap={8}>
                  <Radio.Group
                    value={tcContentMode}
                    size="small"
                    onChange={(e) => {
                      const mode = e.target.value;
                      setTcContentMode(mode);
                      if (mode === "default") {
                        form.setFieldValue(
                          "terms_and_conditions",
                          defaultTcText,
                        );
                      } else {
                        form.setFieldValue("terms_and_conditions", "");
                      }
                    }}
                  >
                    <Radio value="default">Use Default</Radio>
                    <Radio value="custom">Write Custom</Radio>
                  </Radio.Group>
                  {/* <Form.Item
                    name="terms_and_conditions"
                    noStyle
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const div = document.createElement("div");
                          div.innerHTML = value;
                          const plain = div.textContent || div.innerText || "";
                          if (plain.trim().length > 5000) {
                            return Promise.reject(
                              new Error("Maximum 5000 characters allowed"),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    {tcContentMode === "default" ? (
                      <div
                        className={` mt-4 ${styles.customEditor}`}
                        dangerouslySetInnerHTML={{ __html: defaultTcText }}
                      />
                    ) : (
                      <ReactQuill
                        className={` mt-4 ${styles.customEditor}`}
                        theme="snow"
                        onChange={(_content, _delta, source, editor) => {
                          setTcCharCount(
                            Math.max(0, editor.getText().length - 1),
                          );
                          if (source === "user") setTcContentMode("custom");
                        }}
                        modules={{
                          toolbar: [
                            ["bold", "italic", "underline"],
                            [{ list: "ordered" }, { list: "bullet" }],
                          ],
                        }}
                      />
                    )}
                  </Form.Item> */}
                  <Form.Item
                    name="terms_and_conditions"
                    noStyle
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();

                          const div = document.createElement("div");
                          div.innerHTML = value;

                          const plain = div.textContent || div.innerText || "";

                          if (plain.trim().length > MAX_LENGTH) {
                            return Promise.reject(
                              new Error(
                                `Maximum ${MAX_LENGTH} characters allowed`,
                              ),
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    {tcContentMode === "default" ? (
                      <div className={`mt-4 ${styles.readOnlyEditor}`}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: defaultTcText,
                          }}
                        />
                      </div>
                    ) : (
                      <ReactQuill
                        ref={tcQuillRef}
                        className={`mt-4 ${styles.customEditor}`}
                        theme="snow"
                        onChange={(_content, _delta, source, editor) => {
                          const text = editor.getText().replace(/\n$/, "");

                          const length = text.length;

                          if (length > MAX_LENGTH) {
                            const quill = tcQuillRef.current?.getEditor();

                            quill?.deleteText(MAX_LENGTH, length);

                            setTcCharCount(MAX_LENGTH);
                            return;
                          }

                          setTcCharCount(length);

                          if (source === "user") {
                            setTcContentMode("custom");
                          }
                        }}
                        modules={{
                          toolbar: [
                            ["bold", "italic", "underline"],
                            [{ list: "ordered" }, { list: "bullet" }],
                          ],
                        }}
                      />
                    )}
                  </Form.Item>
                  {tcContentMode === "custom" && (
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: 12,
                        color:
                          tcCharCount > 5000 ? "#ff4d4f" : "rgba(0,0,0,0.45)",
                      }}
                    >
                      {tcCharCount}/5000
                    </div>
                  )}
                </Flex>
              </Form.Item>
            )}
          </SectionCard>

          {/* ── Section: Rules ── */}
          <SectionCard
            title="Event Rules"
            description="Set the rules participants must follow during the event"
          >
            <Form.Item label="Rules" style={{ marginBottom: 0 }}>
              <Flex vertical gap={8}>
                <Radio.Group
                  value={rulesContentMode}
                  size="small"
                  onChange={(e) => {
                    const mode = e.target.value;
                    setRulesContentMode(mode);
                    if (mode === "default") {
                      form.setFieldValue("rules", defaultRulesText);
                    } else {
                      form.setFieldValue("rules", "");
                    }
                  }}
                >
                  <Radio value="default">Use Default</Radio>
                  <Radio value="custom">Write Custom</Radio>
                </Radio.Group>
                <Form.Item
                  name="rules"
                  noStyle
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        const div = document.createElement("div");
                        div.innerHTML = value;

                        const plain = div.textContent || div.innerText || "";

                        if (plain.trim().length > MAX_LENGTH) {
                          return Promise.reject(
                            new Error(
                              `Maximum ${MAX_LENGTH} characters allowed`,
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  {rulesContentMode === "default" ? (
                    <div className={`mt-4 ${styles.readOnlyEditor}`}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: defaultRulesText,
                        }}
                      />
                    </div>
                  ) : (
                    <ReactQuill
                      ref={rulesQuillRef}
                      className={`mt-4 ${styles.customEditor}`}
                      theme="snow"
                      onChange={(_content, _delta, source, editor) => {
                        const text = editor.getText().replace(/\n$/, "");

                        const length = text.length;

                        if (length > MAX_LENGTH) {
                          const quill = rulesQuillRef.current?.getEditor();

                          quill?.deleteText(MAX_LENGTH, length);

                          setRulesCharCount(MAX_LENGTH);
                          return;
                        }

                        setRulesCharCount(length);

                        if (source === "user") {
                          setRulesContentMode("custom");
                        }
                      }}
                      modules={{
                        toolbar: [
                          ["bold", "italic", "underline"],
                          [{ list: "ordered" }, { list: "bullet" }],
                        ],
                      }}
                    />
                  )}
                </Form.Item>
                {rulesContentMode === "custom" && (
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: 12,
                      color:
                        rulesCharCount > 5000 ? "#ff4d4f" : "rgba(0,0,0,0.45)",
                    }}
                  >
                    {rulesCharCount}/5000
                  </div>
                )}
              </Flex>
            </Form.Item>
          </SectionCard>

          {/* ── Section: QR Code ── */}
          <SectionCard
            title="QR Code"
            description="Automatically generate a QR code for participants on registration"
          >
            <Form.Item
              name="dynamic_qr"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>
                <span className="font-medium text-[13px]">
                  Generate a QR code for each participant on registration
                </span>
              </Checkbox>
            </Form.Item>
          </SectionCard>

          {/* ── Footer Actions ── */}
          <div className="flex justify-end gap-3 pt-4 pb-8">
            <Button
              type="primary"
              htmlType="submit"
              disabled={isAnyImageUploading || isEventUnderAdminReview(event)}
              iconPosition="end"
            >
              {event?.status === "draft" ? "Save & Continue" : "Update"}
            </Button>
          </div>
        </Form>
        </fieldset>
      </div>
    </div>
    </Loader>
  );
};

export default AddEventDetails;
